import React from "react";
import { SOURCE_COLORS } from "@/components/superadmin/RevenueMonthlyChart";
import { CreditCard, HandCoins, Truck, Rocket, CircleEllipsis, FileText, HandHeart } from "lucide-react";

const SOURCES = [
  { key: "subscriptions", label: "Abonnements", hint: "Abonnements + factures mensuelles", Icon: CreditCard, color: SOURCE_COLORS.subscriptions },
  { key: "b2c_declaration", label: "Frais de déclaration B2C", hint: "Publication / renouvellement d'un avis de perte public", Icon: FileText, color: SOURCE_COLORS.b2c_declaration },
  { key: "finder_fees", label: "Frais trouveur", hint: "Contribution payée par le trouveur sur une déclaration publique", Icon: HandHeart, color: SOURCE_COLORS.finder_fees },
  { key: "recovery", label: "Frais de récupération", hint: "Déblocage des coordonnées d'un trouveur public", Icon: HandCoins, color: SOURCE_COLORS.recovery },
  { key: "shipping", label: "Expéditions", hint: "Marge findloop uniquement (hors coût Sendcloud)", Icon: Truck, color: SOURCE_COLORS.shipping },
  { key: "boost_b2b", label: "Boost B2B", hint: "Mise en avant des fiches partenaires dans l'annuaire", Icon: Rocket, color: SOURCE_COLORS.boost_b2b },
  { key: "boost_b2c", label: "Boost B2C", hint: "Mise en avant des annonces publiques", Icon: Rocket, color: SOURCE_COLORS.boost_b2c },
  { key: "other", label: "Autres", Icon: CircleEllipsis, color: SOURCE_COLORS.other },
];
const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;

function SendcloudLine({ paid, cost, estimated, testid }) {
  return (
    <span className="block text-[11px] leading-4 text-slate-500 font-normal" data-testid={testid}>
      Reversé à Sendcloud : <b className="text-slate-700">{eur(cost)}</b>{estimated > 0 && <span className="text-amber-700"> (dont {eur(estimated)} estimé)</span>}
      <br />Payé par les clients : {eur(paid)}
    </span>
  );
}

export default function RevenueBreakdown({ bySource, total, month, sendcloud }) {
  if (!bySource) return null;
  return (
    <section className="bg-white rounded-2xl border border-border card-shadow p-5 mb-6" data-testid="revenue-breakdown">
      <h3 className="font-display font-bold text-lg text-findo-navy">Revenus par source</h3>
      <p className="text-sm text-slate-500">Paiements encaissés — total depuis le début et mois en cours.</p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="py-2 pr-3">Source</th><th className="py-2 px-3 text-right">Total</th><th className="py-2 px-3 w-1/3">Part du total</th><th className="py-2 pl-3 text-right">Ce mois-ci</th></tr>
          </thead>
          <tbody>
            {SOURCES.filter(({ key }) => key !== "other" || (bySource.other?.total || 0) > 0).map(({ key, label, hint, Icon, color }) => {
              const v = bySource[key] || { total: 0, month: 0 };
              const pct = total ? Math.round((v.total / total) * 1000) / 10 : 0;
              return (
                <tr key={key} className="border-t border-border" data-testid={`revenue-source-${key}`}>
                  <td className="py-2.5 pr-3 align-top"><span className="flex items-center gap-2 font-semibold text-slate-800"><Icon size={16} style={{ color }} />{label}</span>{hint && <span className="block text-xs text-slate-400 ml-6">{hint}</span>}</td>
                  <td className="py-2.5 px-3 text-right font-bold text-slate-900 whitespace-nowrap" data-testid={`revenue-source-${key}-total`}>{eur(v.total)}
                    {key === "shipping" && sendcloud && <SendcloudLine paid={sendcloud.paid_total} cost={sendcloud.cost_total} estimated={sendcloud.estimated_total} testid="sendcloud-total" />}</td>
                  <td className="py-2.5 px-3"><div className="flex items-center gap-2"><div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full" style={{ width: `${pct}%`, background: color }} /></div><span className="text-xs text-slate-500 w-12 text-right">{pct} %</span></div></td>
                  <td className="py-2.5 pl-3 text-right font-semibold text-slate-700 whitespace-nowrap" data-testid={`revenue-source-${key}-month`}>{eur(v.month)}
                    {key === "shipping" && sendcloud && <SendcloudLine paid={sendcloud.paid_month} cost={sendcloud.cost_month} estimated={sendcloud.estimated_month} testid="sendcloud-month" />}</td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-border font-bold" data-testid="revenue-source-sum">
              <td className="py-2.5 pr-3">Total</td><td className="py-2.5 px-3 text-right">{eur(total)}</td><td /><td className="py-2.5 pl-3 text-right">{eur(month)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}
